/**
 * Feature Flag Utilities
 *
 * Provides efficient feature flag checking with localStorage override support.
 * Caches localStorage values to avoid repeated JSON parsing on every check.
 */

import { FORCE_ON_KEY, FORCE_OFF_KEY } from '~/containers/FeatureFlagManager/constants';
import { isDevEnv } from './environment';

// Cache for localStorage overrides
let cachedOverrides = null;
let cacheTimestamp = 0;
const CACHE_TTL_MS = 1000; // Re-read localStorage at most once per second

/**
 * Read and parse localStorage flag arrays with error handling
 */
const readFlagArray = (key) => {
    try {
        const value = localStorage.getItem(key);
        if (!value) return [];
        const parsed = JSON.parse(value);
        // Ensure we return an array even if localStorage contains other JSON types
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

/**
 * Get cached localStorage overrides, refreshing if stale
 */
const getOverrides = () => {
    const now = Date.now();

    if (!cachedOverrides || now - cacheTimestamp > CACHE_TTL_MS) {
        cachedOverrides = {
            forcedOn: readFlagArray(FORCE_ON_KEY),
            forcedOff: readFlagArray(FORCE_OFF_KEY),
        };
        cacheTimestamp = now;
    }

    return cachedOverrides;
};

/**
 * Invalidate the cache (call after modifying localStorage)
 */
export const invalidateFeatureFlagCache = () => {
    cachedOverrides = null;
    cacheTimestamp = 0;
};

/**
 * Check if a specific feature flag is active
 *
 * Priority:
 * 1. localStorage forcedOn overrides -> true
 * 2. localStorage forcedOff overrides -> false
 * 3. APP_INFO.feature_flags backend value
 *
 * @param {string} flag - The feature flag name to check
 * @returns {boolean} Whether the flag is enabled
 */
export const checkFeatureFlag = (flag) => {
    if (!flag) {
        return false;
    }

    // Only apply localStorage overrides in dev environments
    // This prevents users from bypassing feature flags in production
    if (isDevEnv()) {
        const overrides = getOverrides();

        if (overrides.forcedOn.includes(flag)) {
            return true;
        }

        if (overrides.forcedOff.includes(flag)) {
            return false;
        }
    }

    // Fall back to backend value from APP_INFO
    // APP_INFO is a global set by the backend in variables.js
    return !!window.APP_INFO?.feature_flags?.[flag];
};

// Listen for storage changes from other tabs
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (event.key === FORCE_ON_KEY || event.key === FORCE_OFF_KEY) {
            invalidateFeatureFlagCache();
        }
    });
}
